/**
 * imagem.ts — Compressão de imagem no cliente, antes do upload.
 *
 * Por quê: as fotos são tiradas no celular do instalador, em campo (4G ruim).
 * Reduzir ANTES de subir economiza o upload dele, o disco do VPS e a carga do
 * backend. A qualidade não precisa ser alta — as fotos vão para o PDF impresso.
 *
 * Robusto por design: só mexe em imagens, pula arquivos já leves, e em QUALQUER
 * falha devolve o arquivo original (nunca bloqueia o envio).
 */

const MAX_LADO = 1600; // maior lado, em px (suficiente p/ impressão no PDF)
const QUALIDADE = 0.75; // JPEG
const LIMIAR_BYTES = 512 * 1024; // abaixo disso não vale a pena comprimir

function trocarParaJpg(nome: string): string {
  const base = nome.replace(/\.[^./\\]+$/, '');
  return `${base || 'foto'}.jpg`;
}

/**
 * Comprime uma imagem (redimensiona + re-encoda JPEG). Devolve um novo File;
 * se não for imagem, já for leve, ou algo falhar, devolve o arquivo original.
 */
export async function comprimirImagem(file: File): Promise<File> {
  try {
    if (!file.type.startsWith('image/')) return file; // PDF/doc passam intactos
    if (file.size <= LIMIAR_BYTES) return file; // já leve

    // imageOrientation: 'from-image' aplica a rotação EXIF (foto de celular).
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const escala = Math.min(1, MAX_LADO / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * escala);
    const h = Math.round(bitmap.height * escala);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALIDADE),
    );
    // Só usa o resultado se realmente ficou menor.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], trocarParaJpg(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file; // navegador antigo / imagem corrompida → sobe o original
  }
}
